/**
 * The career game flow (M2 plan §6 M2-22), headless: what the career feature (`src/features/career/feature.ts`,
 * order `FEATURE_ORDER.career`) does, over a narrow slice of the game context so it runs in Node tests.
 *
 * - **Init** (`createCareerFlow`): loads the M2 data, wires the player ship's derived stats (the pristine Kestrel
 *   def and the two player weapon defs, appended before the fx, HUD and audio features build their per-weapon
 *   tables), builds the space world and registers its stages, creates the career runtime (which owns the UI store),
 *   provides the `m2` service, holds the sim on the title and replaces "Click to fly" with the title screen. When the
 *   career runs in the Reach, boot's `careerReach` service makes the runtime's Reach link (M3 R22k: saves, docked
 *   loads and docked respawn).
 * - **Deferred assets:** the station and prop models (`app.json deferredAssetPrefixes`) load after the first frame
 *   through the boot loader; each lands in the live models map, each station model is attached to its station, and
 *   Launch unlocks when all are in (`assetsReady`, progress in the store).
 * - **Streamed ships** (M10 P1b2): every ship model the `ships` stream lands joins the live models map too, so wreck
 *   views (which read that map and skip a missing model) see streamed hulls.
 * - **beforeTicks** (`flowBeforeTicks`): the M2 edge actions, wheel and D-pad power steps, the scan key and the
 *   flight axes into `space.input`; in a tool mode the M1 `target` and `cyclePrimary` edges are consumed here.
 * - **afterTick:** the runtime's tick (events → commands, due times, the death rebuild, autosaves).
 * - **frame** (`flowFrame`): starts the deferred loads on the first frame and republishes the UI snapshot at most
 *   `career.json snapshotHz` times a second.
 *
 * The per-frame functions allocate nothing (D-014).
 */
import type { Services } from '../app/services'
import { AssetLoadError } from '../assets/boot'
import type { AssetLoader, LoadedModel } from '../assets/loader'
import type { ShipStream } from '../assets/shipStream'
import { logger } from '../core/logger'
import type { GameData } from '../data'
import type { AssetsFile } from '../data/assetSchema'
import { loadM2Data } from '../data/m2'
import type { M2Data } from '../data/m2Schemas'
import type { Bindings } from '../data/schemas'
import { drainPressed, drainWheel, scanHeld, type InputDevices } from '../input/devices'
import { createJoystick, type Joystick } from '../input/joystick'
import { createPlayerControl, flightAxisMagnitude, readPlayerControl, type PlayerControl } from '../input/playerInput'
import type { OpenedStorage } from '../save/storage'
import type { Sim } from '../sim'
import { clearToolInputEdges, TOOL, type M2Runtime, type SpaceWorld } from '../space/contracts'
import { createSpaceWorld, registerSpaceStages } from '../space/world'
import { strategicMarketFor } from '../strategic-link/market'
import { m2IdOfSite } from '../bridge/m2/stations'
import type { SystemLayout } from '../galaxy/layout/types'
import type { CmdOutcome } from '../ui/contracts'
import {
  CAREER_TEXTS,
  createCareerRuntime,
  fillText,
  HOLD,
  setupPlayerShip,
  type CareerReachLink,
  type CareerRuntime,
  type DevCommand,
  type PlayerShipSetup,
} from './runtime'

/** What the career's Reach link reads and writes through (forwards to the career runtime once the flow built it). */
export type CareerLinkTarget = Pick<CareerRuntime, 'active' | 'career' | 'applyDevCommand' | 'autosave'>

/**
 * Makes the career's Reach link (M3 R22k). Boot provides it as the `careerReach` service when the career runs in the
 * Reach; the flow calls it once, before the career runtime exists, so `target` forwards lazily.
 */
export type CareerReachFactory = (target: CareerLinkTarget, data: M2Data) => CareerReachLink

declare module '../app/services' {
  interface ServiceMap {
    /** The career's Reach link maker (the career in the Reach only), provided by boot (M3 R22k). */
    careerReach: CareerReachFactory
  }
}

/** The slice of `GameContext` the flow uses. */
export interface CareerFlowHost {
  readonly sim: Sim
  readonly data: GameData
  readonly assets: AssetsFile
  /** Models boot loaded (the ships); the deferred ones are added to the flow's own live map. */
  readonly models: ReadonlyMap<string, LoadedModel>
  readonly loader: Pick<AssetLoader, 'loadModel'>
  readonly holds: Set<string>
  readonly services: Services
  /** R3-1 sweep: the Reach runtime (its live layout maps home-cluster site ids to M2's ids), when there is one. */
  readonly reach?: { readonly session: { readonly layout: SystemLayout | null } } | null
  startOverride: ((begin: () => void) => void) | null
}

/** Flow tuning (`src/features/career/career.json`). */
export interface CareerFlowTuning {
  /** A flight axis beyond this counts as flying (`PlayerToolInput.flightActive` cancels auto-dock). */
  flightActiveThreshold: number
  /** Station notices kept on screen. */
  maxNotices: number
}

export interface CareerFlowOptions {
  /** True for asset ids boot skipped (`app.json deferredAssetPrefixes`). */
  isDeferred: (id: string) => boolean
  tuning: CareerFlowTuning
  /** Clears boot's loading panel from `#overlay` (the title screen replaces "Click to fly"). */
  hideOverlay?: () => void
  /** Space world seed before any career exists (a career re-seeds it). Default: `world.json` seed. */
  worldSeed?: number
  openStorage?: () => Promise<OpenedStorage>
  now?: () => Date
  randomSeed?: () => number
}

/**
 * The `m2` service with the dev hook M2-23's tools use (`applyDevCommand`), and the strategic goods trade by station
 * id that the Reach's Parts tab buys relay parts through (economy plan SY-2e).
 */
export interface CareerService extends M2Runtime {
  applyDevCommand(name: string, cmd: DevCommand): CmdOutcome
  /** Whether the strategic market runs (the strategic service exists). */
  goodsMarketRuns(): boolean
  quoteGood: CareerRuntime['quoteGood']
  buyGood: CareerRuntime['buyGood']
  /** Additive (D-617 A6): the pause menu's Quit to title autosaves, then shows the title. */
  autosave: CareerRuntime['autosave']
  showTitle: CareerRuntime['showTitle']
}

/** What `flowBeforeTicks` reads and writes. */
export interface FlowInputState {
  readonly space: Pick<SpaceWorld, 'input' | 'player' | 'docking'>
  /** Scratch control frame for the flight-axis check. */
  readonly control: PlayerControl
  readonly threshold: number
  /** A centred stick, read in place of the mouse stick on a dock-press frame (CL-1). Never written. */
  readonly still: Joystick
  /** Last frame's state: held, and the docking readout in phase `autodock` (CL-1 edges). */
  wasHeld: boolean
  wasAutoFlying: boolean
}

/** Puts the mouse virtual stick back at centre. */
function centreStick(j: Joystick): void {
  j.x = 0
  j.y = 0
}

/** What `flowFrame` reads and writes. */
export interface FlowFrameState {
  readonly runtime: Pick<CareerRuntime, 'dirty' | 'publish'>
  /** Milliseconds between snapshot publishes (1000 / `snapshotHz`). */
  readonly publishMs: number
  lastPublish: number
  assetsStarted: boolean
  readonly startAssets: () => void
}

export interface CareerFlow {
  readonly data: M2Data
  readonly space: SpaceWorld
  readonly runtime: CareerRuntime
  readonly service: CareerService
  readonly ship: PlayerShipSetup
  /** The live models map (boot's models plus the deferred ones as they load). */
  readonly models: Map<string, LoadedModel>
  readonly inputState: FlowInputState
  readonly frameState: FlowFrameState
  /** Loads the deferred station and prop models (the first frame starts it once; resolves when every one is in). */
  loadDeferredAssets(): Promise<void>
  /** Unregisters the space stages. */
  dispose(): void
}

/**
 * Once per frame when not paused (also while held), before the M1 combat edges and the ticks. While held (docked,
 * title, a moment screen) the tool edges are dropped: the station screens own the keys.
 */
export function flowBeforeTicks(st: FlowInputState, dev: InputDevices, bindings: Bindings, held: boolean): void {
  const ti = st.space.input
  // CL-1 (D-TBD): the mouse stick never re-centres, and UI clicks (pointer unlocked, or a test build) move it. Leaving
  // a held screen (the launch, the title, a moment) and an auto-flight starting (bay auto-dock, moor, cradle) both
  // centre it, as the pad handover does (pollGamepad), so only fresh deflection steers or cancels the auto-flight.
  const autoFlying = st.space.docking.readout.phase === 'autodock'
  if ((st.wasHeld && !held) || (autoFlying && !st.wasAutoFlying)) centreStick(dev.joystick)
  st.wasHeld = held
  st.wasAutoFlying = autoFlying
  if (held) {
    drainPressed(dev, 'toolMining')
    drainPressed(dev, 'toolSalvage')
    drainPressed(dev, 'toolCycle')
    drainPressed(dev, 'dock')
    drainPressed(dev, 'powerUp')
    drainPressed(dev, 'powerDown')
    drainWheel(dev)
    clearToolInputEdges(ti)
    ti.scanHeld = false
    ti.flightActive = false
    return
  }
  if (drainPressed(dev, 'toolMining')) ti.modeRequest = TOOL.mining
  if (drainPressed(dev, 'toolSalvage')) ti.modeRequest = TOOL.salvage
  // The jettison offer owns the pad R3 tap while it is open: drain the edge without cycling the tool.
  if (drainPressed(dev, 'toolCycle') && !ti.cycleSuppressed) ti.cycleMode = true
  if (drainPressed(dev, 'dock')) ti.dockPressed = true
  // A dock press may start an auto-flight on the tick that consumes it, which docking.control would cancel on that
  // same tick for a stick left off centre: until a tick consumes the press (a frame can run none), the flight check
  // leaves the mouse stick out, and the frame after the start centres it (above).
  const dockPress = ti.dockPressed && !autoFlying
  let steps = drainWheel(dev)
  if (drainPressed(dev, 'powerUp')) steps += 1
  if (drainPressed(dev, 'powerDown')) steps -= 1
  ti.powerSteps += steps
  ti.scanHeld = scanHeld(dev, bindings)
  readPlayerControl(dev.keys, dev.pad, dockPress ? st.still : dev.joystick, bindings, st.control, dev.mouseButtons)
  ti.flightActive = flightAxisMagnitude(st.control) > st.threshold
  if (st.space.player.mode !== TOOL.combat) {
    // Tool modes own the target key (nav pick) and the pad X button (scan): the M1 edges never see them.
    if (drainPressed(dev, 'target')) ti.targetPressed = true
    drainPressed(dev, 'cyclePrimary')
  }
}

/** Once per rendered frame: the deferred loads on the first frame, then the snapshot at most `snapshotHz`. */
export function flowFrame(st: FlowFrameState, nowMs: number): void {
  if (!st.assetsStarted) {
    st.assetsStarted = true
    st.startAssets()
  }
  if (st.runtime.dirty && nowMs - st.lastPublish >= st.publishMs) {
    st.lastPublish = nowMs
    st.runtime.publish()
  }
}

/**
 * M10 P1b2: copies every model `ships` lands into `models` (the career's live map), so wreck views see streamed
 * ships. Returns the unsubscribe. Rare (a landed model).
 */
export function mirrorStreamedModels(ships: Pick<ShipStream, 'get' | 'onLoaded'>, models: Map<string, LoadedModel>): () => void {
  return ships.onLoaded((id) => {
    const model = ships.get(id)
    if (model !== undefined) models.set(id, model)
  })
}

export function createCareerFlow(host: CareerFlowHost, opts: CareerFlowOptions): CareerFlow {
  const data = loadM2Data(host.data)
  const ship = setupPlayerShip(host.sim, data)
  const models = new Map<string, LoadedModel>(host.models)
  const ships = host.services.maybe('ships')
  const offShips = ships === undefined ? null : mirrorStreamedModels(ships, models)
  const space = createSpaceWorld(host.sim, host.data, data, models, opts.worldSeed ?? host.data.world.seed)
  const offStages = registerSpaceStages(host.sim, space)
  let begin: () => void = () => undefined
  // The career in the Reach (M3 R22k): the link is made before the runtime, so its target forwards once bound.
  let bound: CareerRuntime | null = null
  const live = (): CareerRuntime => {
    if (bound === null) throw new Error('career: the runtime is not built yet')
    return bound
  }
  const target: CareerLinkTarget = {
    get active(): boolean {
      return bound?.active === true
    },
    career: () => live().career(),
    applyDevCommand: (name, cmd) => live().applyDevCommand(name, cmd),
    autosave: () => live().autosave(),
  }
  const runtime = createCareerRuntime({
    reach: host.services.maybe('careerReach')?.(target, data),
    // M4 plan I3: the strategic market behind quote() and the trades, once the strategic service exists (reach mode).
    strategicMarket: () => {
      const client = host.services.maybe('strategic')
      return client === undefined ? null : strategicMarketFor(client)
    },
    // M8 TR-1b: the price log records from the strategic client's views and clock (reach mode).
    priceFeed: () => host.services.maybe('strategic') ?? null,
    stationIdOf: (siteId) => m2IdOfSite(siteId, host.reach?.session.layout ?? null),
    space,
    data,
    ship,
    holds: host.holds,
    begin: () => begin(),
    maxNotices: opts.tuning.maxNotices,
    openStorage: opts.openStorage,
    now: opts.now,
    randomSeed: opts.randomSeed,
  })
  bound = runtime
  const service: CareerService = {
    data,
    space,
    career: () => runtime.career(),
    ui: runtime.ui,
    models,
    assetsReady: false,
    applyDevCommand: (name, cmd) => runtime.applyDevCommand(name, cmd),
    goodsMarketRuns: () => host.services.maybe('strategic') !== undefined,
    quoteGood: (stationId, good, qty, side) => runtime.quoteGood(stationId, good, qty, side),
    buyGood: (stationId, good, qty) => runtime.buyGood(stationId, good, qty),
    autosave: () => runtime.autosave(),
    showTitle: () => runtime.showTitle(),
  }
  host.services.provide('m2', service)

  // The title screen replaces "Click to fly"; the sim stays held until a career starts.
  host.holds.add(HOLD.title)
  host.startOverride = (start: () => void): void => {
    begin = start
    opts.hideOverlay?.()
    runtime.showTitle()
  }

  function attachStations(id: string, model: LoadedModel): void {
    const list = space.stations.list
    for (let i = 0; i < list.length; i++) if (list[i].def.assetId === id) space.stations.attachModel(i, model)
  }

  let loading: Promise<void> | null = null
  async function loadDeferred(): Promise<void> {
    const entries = host.assets.models.filter((e) => opts.isDeferred(e.id) && !models.has(e.id))
    const total = entries.length
    let done = 0
    runtime.setAssets(0, false)
    try {
      await Promise.all(
        entries.map(async (entry) => {
          let model: LoadedModel
          try {
            model = await host.loader.loadModel(entry)
          } catch (err) {
            throw new AssetLoadError(entry.id, err)
          }
          models.set(entry.id, model)
          attachStations(entry.id, model)
          done++
          runtime.setAssets(done / total, false)
        }),
      )
    } catch (err) {
      const id = err instanceof AssetLoadError ? err.assetId : 'unknown'
      logger.error(`career: deferred model ${id} failed to load`, err)
      runtime.notify('error', fillText(CAREER_TEXTS.assetsFailed, { id }))
      return
    }
    // Every station model must be in: a station without one could never dock or launch.
    for (const s of space.stations.list) if (!s.ready) logger.warn(`career: station ${s.def.id} has no model after loading`)
    service.assetsReady = true
    runtime.setAssets(1, true)
  }

  const flow: CareerFlow = {
    data,
    space,
    runtime,
    service,
    ship,
    models,
    inputState: {
      space,
      control: createPlayerControl(),
      threshold: opts.tuning.flightActiveThreshold,
      still: createJoystick(),
      wasHeld: false,
      wasAutoFlying: false,
    },
    frameState: {
      runtime,
      publishMs: 1000 / data.career.snapshotHz,
      lastPublish: Number.NEGATIVE_INFINITY,
      assetsStarted: false,
      startAssets: () => {
        void flow.loadDeferredAssets()
      },
    },
    loadDeferredAssets(): Promise<void> {
      loading ??= loadDeferred()
      return loading
    },
    dispose(): void {
      offStages()
      offShips?.()
    },
  }
  return flow
}
